import { AnimatePresence } from "motion/react";
import * as motion from "motion/react-m";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { customSanitize } from "../../utils/customSanitize";

import styles from "./PopUp.module.scss";
import { useAppContext } from "../../context/AppContext";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { ReactSVG } from "react-svg";
import { opacity_variant } from "../../utils/animation/transitions";

import { useLenis } from "lenis/react";
import { FocusTrap } from "@aleksundshantu.com/aunds-utils-react/accessibility";
import { PopUpType } from "@/utils/getPopUpData";
import { Button } from "../Global/Button/Button";
import { useCookies } from "react-cookie";
import { MediaItem } from "../Global/MediaItem/MediaItem";

interface PopUpProps {
  extraClassName?: string;
  popupData: PopUpType;
}

export const AddProperty = (
  elements: HTMLCollectionOf<HTMLElement>,
  property: string,
  value: string,
) => {
  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    if (element.style) element.style.setProperty(property, value);
  }
};

const PopUp = forwardRef<HTMLDivElement, PopUpProps>(
  ({ extraClassName = "", popupData, ...props }, ref) => {
    const innerRef = useRef<HTMLDivElement | null>(null);
    const { show_popup, delaytime, popup } = popupData;

    const { openPopup, setopenPopup } = useAppContext();
    const popupId =
      Array.isArray(popup) && popup.length ? popup[0].toString() : "";

    const [allCookies, setCookie] = useCookies([
      "aunds-cookie",
      `popup-closed-${popupId}`,
    ]);
    const showPopup =
      openPopup && show_popup && !allCookies[`popup-closed-${popupId}`];
    const { getIcon, getLabel } = useGlobalContext();

    const { content, link, title, image, image_mobile } =
      popupData &&
      popupData.popUpData?.acf &&
      "header_popup" in popupData.popUpData.acf
        ? popupData.popUpData.acf.header_popup
        : {};

    const lenis = useLenis();

    useImperativeHandle(ref, () => innerRef.current as HTMLDivElement);

    const close = () => {
      setopenPopup(false);
      setCookie(`popup-closed-${popupId}`, true, { path: "/" });
      lenis?.start();
    };
    useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          close();
        }
      };

      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
      let tid: NodeJS.Timeout;
      if (
        !showPopup &&
        !allCookies[`popup-closed-${popupId}`] &&
        popupId &&
        delaytime >= 0
      ) {
        tid = setTimeout(() => {
          setopenPopup(true);
          // setCookie(`popup-closed-${popupId}`, true, { path: "/" });
          lenis?.stop();
          clearTimeout(tid);
        }, delaytime * 1000);
      }

      return () => {
        clearTimeout(tid);
      };

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [popupId]);

    useEffect(() => {
      const htmlTag = document.documentElement;
      const scrollBarWidth = window.innerWidth - htmlTag.clientWidth;
      const tagsToAddmargin = document.getElementsByClassName(
        "scrollbar-width",
      ) as HTMLCollectionOf<HTMLElement>;
      if (openPopup) {
        if (scrollBarWidth !== 0) {
          AddProperty(
            tagsToAddmargin,
            "width",
            `calc(100% - ${scrollBarWidth}px)`,
          );
          htmlTag.style.marginRight = `${scrollBarWidth}px`;
        }
        lenis?.stop();
      } else {
        AddProperty(tagsToAddmargin, "width", `100%`);
        htmlTag.style.marginRight = `unset`;
        lenis?.start();
      }
    }, [openPopup, lenis]);

    if (!showPopup) return null;
    return (
      <>
        <AnimatePresence mode="wait">
          {showPopup && (
            <FocusTrap autoFocus>
              <motion.div
                id="popup"
                variants={opacity_variant}
                initial="initial"
                animate="animate"
                exit={"exit"}
                ref={innerRef}
                {...props}
                role="dialog"
                aria-modal={showPopup}
                // aria-labelledby="popup-title" // ID modal title ??
                // aria-describedby={aria_description ?? "popup-script"}
                //! please dont remove scrollbar-width class
                className={`${styles["pop-up"]} ${extraClassName} scrollbar-width`}
              >
                <button
                  onClick={() => close()}
                  type="button"
                  aria-label={getLabel("modal_close")}
                  className={`${styles["close-btn"]} navbar-menu-burger-close`}
                >
                  <ReactSVG src={getIcon("close")} wrapper={"svg"} />
                </button>

                <section
                  data-lenis-prevent
                  className={`section ${styles["popup-section"]}`}
                >
                  <div data-lenis-prevent className="container">
                    <div className="columns is-centered m-0">
                      <div
                        className={`column has-weiss-bg is-12-tablet is-10-widescreen is-8-ultrahd ${styles["popup-wrapper"]}`}
                      >
                        <div className={`${styles["text-col"]}`}>
                          {title && <h2 className="h3">{title}</h2>}

                          {content && (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: customSanitize(content),
                              }}
                            />
                          )}

                          {link && link.url && (
                            <Button
                              type="primary"
                              href={link.url}
                              target={link.target}
                              className={`${styles["popup-button"]}`}
                              icon={
                                link.target === "_blank"
                                  ? "circleExternalLink"
                                  : ""
                              }
                              aria-label={link.aria_label || ""}
                            >
                              {link.title}
                            </Button>
                          )}
                        </div>

                        <div className={`${styles["media-col"]}`}>
                          {image && (
                            <MediaItem
                              image_desktop={image}
                              image_mobile={image_mobile}
                              sizes="(max-width: 767px) 90vw,(max-width: 1023px) 60vw, 40vw"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </motion.div>
            </FocusTrap>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default PopUp;

PopUp.displayName = "PopUp";
